import { useState } from 'react';
import {
  analyzeContractUpload,
  uploadContractFile,
} from './contractDetailApi';

async function sha256(file) {
  const buf = await file.arrayBuffer();
  const hash = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export default function useContractFileUploadWorkflow({
  canWriteContracts,
  contractId,
  refreshContract,
  setShowScanModal,
  showToast,
}) {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadAnalyzing, setUploadAnalyzing] = useState(false);
  const [uploadDragOver, setUploadDragOver] = useState(false);
  const [pendingUpload, setPendingUpload] = useState(null);
  const [rescanInitialFile, setRescanInitialFile] = useState(null);

  const openUploadModal = () => {
    setPendingUpload(null);
    setUploadAnalyzing(false);
    setUploadBusy(false);
    setUploadDragOver(false);
    setShowUploadModal(true);
  };

  const closeUploadModal = () => {
    if (uploadBusy) return;
    setShowUploadModal(false);
    setPendingUpload(null);
  };

  const handleFilePicked = async (file) => {
    if (!file) return;
    setUploadAnalyzing(true);
    setPendingUpload({ file, analyzing: true });
    try {
      const fileHash = await sha256(file);
      const data = await analyzeContractUpload({
        fileHash,
        originalFilename: file.name,
        contextContractId: contractId,
        extracted: {},
      });
      const classification = data?.classification || { classification: 'draft' };
      const detection = data?.detection || {};

      const topVersion = (detection.versionMatches || [])[0];
      const crossContractMatch = topVersion && topVersion.confidence >= 0.85 && topVersion.contractId !== contractId
        ? topVersion
        : null;

      const isMarkup = classification.classification === 'markup';
      const isExecutedSuggestion = classification.classification === 'executed';

      setPendingUpload({
        file,
        fileHash,
        classification,
        crossContractMatch,
        isExecuted: isExecutedSuggestion,
        isReference: false,
        direction: isMarkup ? 'inbound' : '',
        documentDate: '',
        fileNotes: isMarkup ? 'Counterparty markup' : '',
        rescanWithAi: isExecutedSuggestion && canWriteContracts,
        forceUpload: classification.classification !== 'duplicate',
      });
    } catch (err) {
      showToast(err.message || 'Couldn\'t analyze file', 'error');
      setPendingUpload(null);
    } finally {
      setUploadAnalyzing(false);
    }
  };

  const handleConfirmUpload = async () => {
    if (!pendingUpload || !pendingUpload.file) return;
    if (
      pendingUpload.classification?.classification === 'duplicate' &&
      !pendingUpload.forceUpload
    ) {
      showToast('This file is already on this contract. Toggle "upload anyway" to proceed.', 'error');
      return;
    }
    if (pendingUpload.isExecuted && !canWriteContracts) {
      showToast('Only legal/admin can mark a file as executed.', 'error');
      return;
    }
    setUploadBusy(true);
    try {
      const isReference = !!pendingUpload.isReference;
      const form = new FormData();
      form.append('file', pendingUpload.file);
      // Reference documents (doc-46) sit outside the draft/executed ladder —
      // no executed flag, no draft-source naming.
      if (isReference) form.append('fileKind', 'reference');
      if (!isReference && pendingUpload.isExecuted) form.append('isExecuted', 'true');
      if (pendingUpload.direction) form.append('direction', pendingUpload.direction);
      if (pendingUpload.documentDate) form.append('documentDate', pendingUpload.documentDate);
      if (pendingUpload.fileNotes) form.append('fileNotes', pendingUpload.fileNotes);
      if (!isReference) {
        if (pendingUpload.direction === 'inbound') form.append('draftSource', 'their draft');
        else if (pendingUpload.direction === 'outbound') form.append('draftSource', 'our draft');
      }

      await uploadContractFile(contractId, form);
      showToast(
        isReference ? 'Reference document uploaded' : (pendingUpload.isExecuted ? 'Executed version uploaded' : 'File uploaded'),
        'success'
      );
      const fileForRescan = !isReference && pendingUpload.rescanWithAi && canWriteContracts ? pendingUpload.file : null;
      setShowUploadModal(false);
      setPendingUpload(null);
      refreshContract();
      if (fileForRescan) {
        setRescanInitialFile(fileForRescan);
        setShowScanModal(true);
      }
    } catch (err) {
      showToast(err.message || 'Upload failed', 'error');
    } finally {
      setUploadBusy(false);
    }
  };

  return {
    closeUploadModal,
    handleConfirmUpload,
    handleFilePicked,
    openUploadModal,
    pendingUpload,
    rescanInitialFile,
    setPendingUpload,
    setRescanInitialFile,
    setUploadDragOver,
    showUploadModal,
    uploadAnalyzing,
    uploadBusy,
    uploadDragOver,
  };
}
